import { useEffect, useRef, useState } from 'react';
import api from '../../../services/api';
import useAuth from '../../../hooks/useAuth';
import { PageQueryParams } from '../../../domain/response-wrapper';
import { usePagination } from '../../../hooks/usePagination';
import { PaginatedPaymentLinks, PaymentLinkView } from '../models/payment-link-view';
import { BroxelRawPaymentLinksResponse, mapBroxelRawToView } from '../models/broxel-raw-payment-link';
import { PAYMENT_LINKS_ENDPOINTS } from '../links.constants';

/**
 * Params de la Consulta. Además de page/size (0-based, el BFF los pasa al micro), lleva
 * los filtros que ahora sí resuelve el backend: concepto y rango de fecha se aplican DIRECTO
 * en Broxel; monto y estado los aplica el microservicio con repaginado. Todos opcionales.
 */
export interface BroxelPaymentLinksQueryParams extends PageQueryParams {
  concept?: string;
  status?: string;
  amountMin?: string;
  amountMax?: string;
  dateStart?: string;
  dateEnd?: string;
}

export const DEFAULT_BROXEL_PAYMENT_LINKS_PARAMS: BroxelPaymentLinksQueryParams = {
  page: 0,
  size: 10,
};

// Broxel serializa varios campos numéricos como string (ya confirmado con `amount`); se
// coerciona defensivamente en vez de asumir que el JSON trae números reales.
const toNumber = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// Clave estable por combinación exacta de filtros+paginación, para el caché de combos ya
// consultados (ver más abajo). Dos combinaciones con los mismos valores siempre producen
// la misma clave.
const buildCacheKey = (params: BroxelPaymentLinksQueryParams): string => JSON.stringify([
  params.page,
  params.size,
  params.concept ?? null,
  params.status ?? null,
  params.amountMin ?? null,
  params.amountMax ?? null,
  params.dateStart ?? null,
  params.dateEnd ?? null,
]);

const hasNoFilters = (params: BroxelPaymentLinksQueryParams): boolean =>
  !params.concept && !params.status && !params.amountMin && !params.amountMax && !params.dateStart && !params.dateEnd;

const matchesFilters = (record: PaymentLinkView, params: BroxelPaymentLinksQueryParams): boolean => {
  if (params.concept && !record.concept.toLowerCase().includes(params.concept.toLowerCase())) return false;
  if (params.status && record.status !== params.status) return false;
  if (params.amountMin && record.amount < Number(params.amountMin)) return false;
  if (params.amountMax && record.amount > Number(params.amountMax)) return false;

  const createdDate = record.createdAt.slice(0, 10);
  if (params.dateStart && createdDate < params.dateStart) return false;
  if (params.dateEnd && createdDate > params.dateEnd) return false;

  return true;
};

/**
 * Consume `GET bff/payment-links-view/broxel/raw` para el listado completo de "Links de
 * Pago" (Consulta), paginado y con filtros REALES resueltos en el backend (concepto, monto
 * min/máx, estado y rango de fecha).
 *
 * Para no llamar al BFF cuando la info ya está disponible localmente, mantiene dos niveles
 * de caché en memoria (por instancia de la vista — se pierden al desmontarla):
 *
 * 1. Dataset completo: si una consulta SIN filtros (page 0) confirma que todos los
 *    registros del comercio caben en una sola página (`totalPages <= 1`), ya tenemos
 *    absolutamente todo. A partir de ahí, CUALQUIER filtro o cambio de página (sin
 *    importar el valor, incluso uno nunca antes escrito) se resuelve filtrando/paginando
 *    esos registros en memoria — sin volver a llamar al BFF.
 * 2. Combos ya vistos: si el dataset no cabe completo (hay más de una página en el
 *    backend), se cachea cada combinación exacta de filtros+página que ya se consultó,
 *    para no repetir la misma llamada si el usuario vuelve a ella (p. ej. quita un filtro
 *    y lo vuelve a poner igual).
 */
export const useBroxelPaymentLinks = (initialParams: Partial<BroxelPaymentLinksQueryParams> = {}) => {
  const { queryParams, nextPage, prevPage, goToPage, updateParams, search } =
    usePagination<BroxelPaymentLinksQueryParams>({ ...DEFAULT_BROXEL_PAYMENT_LINKS_PARAMS, ...initialParams });

  const { user } = useAuth();
  const fullDatasetRef = useRef<BroxelRawPaymentLinksResponse | null>(null);
  const comboCacheRef = useRef<Map<string, BroxelRawPaymentLinksResponse>>(new Map());
  const [rawResponse, setRawResponse] = useState<BroxelRawPaymentLinksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const full = fullDatasetRef.current;

    if (full) {
      // Ya tenemos TODOS los registros del comercio: se filtra y pagina en memoria, sin
      // llamar al BFF sea cual sea la combinación de filtros.
      const page = toNumber(queryParams.page, 0);
      const size = toNumber(queryParams.size, 10);
      const filteredRecords = full.data.records.filter((record) => matchesFilters(mapBroxelRawToView(record), queryParams));

      setRawResponse({
        ...full,
        data: {
          ...full.data,
          currentPage: page + 1, // mismo 1-indexado del BFF real, para que la normalización de más abajo lo trate igual
          pageSize: size,
          totalRecords: filteredRecords.length,
          totalPages: Math.max(1, Math.ceil(filteredRecords.length / size)),
          records: filteredRecords.slice(page * size, page * size + size),
        },
      });
      setError(null);
      setLoading(false);
      return;
    }

    const cacheKey = buildCacheKey(queryParams);
    const cachedCombo = comboCacheRef.current.get(cacheKey);

    if (cachedCombo) {
      setRawResponse(cachedCombo);
      setError(null);
      setLoading(false);
      return;
    }

    if (!user?.access_token) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    api.get<BroxelRawPaymentLinksResponse>(PAYMENT_LINKS_ENDPOINTS.broxelRaw, {
      params: queryParams,
      signal: controller.signal,
    }).then((response) => {
      if (response.status !== 200) {
        throw new Error(`Error: ${response.status}`);
      }

      // Si esta consulta sin filtros ya trajo TODO (una sola página), se guarda como el
      // dataset completo: de aquí en adelante ningún filtro vuelve a tocar el BFF.
      if (hasNoFilters(queryParams) && toNumber(queryParams.page, 0) === 0 && toNumber(response.data.data.totalPages, 1) <= 1) {
        fullDatasetRef.current = response.data;
      }

      comboCacheRef.current.set(cacheKey, response.data);
      setRawResponse(response.data);
      setError(null);
      setLoading(false);
    }).catch((err) => {
      if (err.name !== 'CanceledError') {
        setError((err as Error).message);
        setLoading(false);
      }
    });

    return () => controller.abort();
  }, [queryParams, user?.access_token]);

  const data: PaginatedPaymentLinks | null = rawResponse
    ? {
      // El BFF real devuelve currentPage 1-indexado (para page=0 enviado en la
      // petición, responde currentPage=1), a diferencia del resto del contrato que es
      // 0-indexado. Se resta 1 para que PaginatorFooter (0-indexado) calcule bien el
      // "Página X de Y", el rango de "Resultados" y el disabled de los botones.
      currentPage: toNumber(rawResponse.data.currentPage, 1) - 1,
      pageSize: toNumber(rawResponse.data.pageSize, Number(queryParams.size)),
      totalRecords: toNumber(rawResponse.data.totalRecords, 0),
      totalPages: toNumber(rawResponse.data.totalPages, 1),
      records: rawResponse.data.records.map(mapBroxelRawToView),
    }
    : null;

  const setPageSize = (size: number) => updateParams({ size, page: 0 });

  // Un filtro simple: reinicia a page 0 (lo hace `search`) y refetch (o resuelve local/caché).
  const setFilter = (key: keyof BroxelPaymentLinksQueryParams, value?: string) =>
    search(key, value || undefined);

  // El rango de fecha actualiza ambos extremos a la vez (y reinicia page).
  const setDateRange = (dateStart?: string, dateEnd?: string) =>
    updateParams({ dateStart: dateStart || undefined, dateEnd: dateEnd || undefined, page: 0 });

  const clearFilters = () =>
    updateParams({
      concept: undefined,
      status: undefined,
      amountMin: undefined,
      amountMax: undefined,
      dateStart: undefined,
      dateEnd: undefined,
      page: 0,
    });

  return {
    queryParams,
    data,
    loading,
    error,
    nextPage,
    prevPage,
    goToPage,
    updateParams,
    setPageSize,
    setFilter,
    setDateRange,
    clearFilters,
  };
};
